import { z } from "zod";
import { EntitlementSummarySchema, IsoDateTimeSchema, LocaleSchema } from "./primitives.js";
import { HardConstraintClarificationSchema } from "./profile.js";
/**
 * Explicit consent to process HEALTH data and to send it to the third-party AI
 * (Apple guideline 5.1.2(i), GDPR Art. 9(2)(a)). The wording the user agreed to is
 * versioned; bump this constant (and migrate nothing — a missing row at the current
 * version simply re-asks) when the provider, the data list or the legal basis changes.
 * ONE place: the API, web and mobile all import it from here.
 */
export const HEALTH_AI_CONSENT_VERSION = "health-ai-2026-10b";
/** consent_records.scope of this consent (migration 0047). */
export const HEALTH_AI_CONSENT_SCOPE = "health_ai";
/**
 * Stable error code of the 403 every health-data / AI endpoint answers when the caller has no
 * active consent at the current version. Clients map it to the consent gate, never to a generic error.
 */
export const HEALTH_AI_CONSENT_REQUIRED_CODE = "HEALTH_AI_CONSENT_REQUIRED";
/** An active (not withdrawn) consent at the CURRENT wording version. */
export const HealthAiConsentSchema = z.object({
    version: z.string(),
    consentedAt: IsoDateTimeSchema,
});
// GET /v1/me — response only, no request schema. `id` here is the server's
// own account identifier echoed back to its owner; it is never client-supplied.
export const MeResponseSchema = z.object({
    id: z.string(),
    locale: LocaleSchema,
    onboardingStatus: z.enum(["not_started", "in_progress", "completed"]),
    onboardingStep: z.number().int().min(1).max(13).nullable(),
    entitlement: EntitlementSummarySchema,
    createdAt: IsoDateTimeSchema,
    /** ADR-080: hard-constraint rows the user still has to confirm (drives the Today line; `[]` for everyone else). */
    pendingClarifications: z.array(HardConstraintClarificationSchema).default([]),
    /**
     * The caller's active health/AI consent at the current wording version, or `null` — in which
     * case the API refuses every health-data write, plan generation, Kitchen extraction and Amy
     * turn with 403 HEALTH_AI_CONSENT_REQUIRED and the apps show the consent screen (fail closed:
     * a response without the field reads as no consent).
     */
    healthAiConsent: HealthAiConsentSchema.nullable().default(null),
});
/**
 * PATCH /v1/me — the write path for `users.locale` (2026-09-10).
 *
 * The API localises catalogue names server-side (`ingredient.names[locale]`,
 * chosen from `users.locale`), but that column was only ever written by the
 * onboarding language answer. The in-app language toggle changed the client
 * dictionary alone, so an account created in EN and later switched to DE kept
 * receiving English ingredient names under German chrome — for good.
 *
 * This is deliberately its own endpoint rather than a field on
 * `PATCH /v1/profile`: locale lives on `users`, not on the profile, and the
 * profile patch is derived from `ProfileSchema` (goals, targets, budget).
 */
export const MePatchRequestSchema = z.object({
    locale: LocaleSchema,
});
/**
 * #100 version handshake: the currently deployed Amy behavior version.
 * Clients rotate a conversation's RUNTIME session id when its bound
 * version differs (history is keyed by the stable conversationId and
 * survives rotation). minCompatibleVersion reserves room for future
 * no-rotation releases; clients MUST rotate when bound < minCompatible.
 */
export const AmyVersionResponseSchema = z.object({
    behaviorVersion: z.string(),
    minCompatibleVersion: z.string(),
});
/**
 * POST /v1/me/health-ai-consent — record (or re-activate after a withdrawal) the caller's consent.
 * `version` must be the CURRENT wording version (HEALTH_AI_CONSENT_VERSION); the server answers 400
 * UNKNOWN_CONSENT_VERSION for any other value, so a stale client can never record consent to wording
 * it did not show.
 *
 * Withdrawal is the same screen's mirror image (GDPR Art. 7(3): as easy as giving it): DELETE
 * /v1/me/health-ai-consent. It closes every consent gate at once AND erases the health data the
 * consent covered, in one transaction — the exact operator procedure of docs/06-operations
 * ("Withdrawal by email"), which stays available as the fallback.
 */
export const HealthAiConsentRequestSchema = z.object({
    version: z.string().min(1).max(64),
});
export const HealthAiConsentResponseSchema = z.object({
    healthAiConsent: HealthAiConsentSchema,
});
/** DELETE /v1/me/health-ai-consent — `withdrawn` is true whether this call or an earlier one closed the consent (replay-safe). */
export const HealthAiConsentWithdrawalResponseSchema = z.object({
    withdrawn: z.literal(true),
});
//# sourceMappingURL=me.js.map